import React, { useState } from 'react'
import { FormattedMessage } from 'react-intl'

import { noop } from 'lodash-es'
import styled, { useTheme } from 'styled-components/native'

import {
  ProviderSelfHarmTaskArguments,
  ProviderTaskType,
  ProviderTriageRiskTaskArguments,
} from '@lyrahealth-inc/shared-app-logic/src/models/providers/Providers'
import { BodyText, Size as BodyTextSize } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/bodyText/BodyText'
import { CheckIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/CheckIcon'
import { LoadingIndicator } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/LoadingIndicator'
import { PrimaryButton } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/primaryButton/PrimaryButton'
import { SecondaryButton } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/secondaryButton/SecondaryButton'
import { tID } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

import ContactSection from './ContactSection'
import DetailedAlertView from './DetailedAlertView'
import SafetyPlanSection from './SafetyPlanSection'
import { RiskAlertPopoverData } from '../hooks/useRiskAlertPopover'

export type RiskAlertPopoverAlert = {
  taskType: ProviderTaskType
  taskArguments?: ProviderTriageRiskTaskArguments | ProviderSelfHarmTaskArguments
  eventDate?: string | null
}

type RiskAlertPopoverProps = {
  alert: RiskAlertPopoverAlert
  data: RiskAlertPopoverData
  clientId?: string
  onMarkAddressed: () => void
  onLogNote: () => void
  isAddressed?: boolean
  onHideRiskBadge?: () => void
  hideAddressedLabel?: boolean
  isClientChartVersion?: boolean
  onReviewRiskSummary?: () => void
  onSafetyPlanPress?: () => void
}

const Container = styled.View(({ theme }) => ({
  width: '100%',
  gap: theme.spacing['16px'],
}))

const ButtonRow = styled.View(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing['16px'],
}))

const FlexPrimaryButton = styled(PrimaryButton)({
  flex: 1,
})

const FlexSecondaryButton = styled(SecondaryButton)({
  flex: 1,
})

const AddressedLabel = styled.View(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing['4px'],
}))

const RiskAlertPopover: React.FC<RiskAlertPopoverProps> = ({
  alert,
  data,
  clientId,
  onMarkAddressed,
  onLogNote,
  isAddressed = false,
  onHideRiskBadge,
  hideAddressedLabel = false,
  isClientChartVersion = false,
  onReviewRiskSummary,
  onSafetyPlanPress,
}) => {
  const { colors } = useTheme()
  const [isDetailExpanded, setIsDetailExpanded] = useState(!isAddressed)

  if (data.isLoading) {
    return (
      <Container testID={tID('RiskAlertPopover-loading')}>
        <LoadingIndicator topPadding={0} />
      </Container>
    )
  }

  return (
    <Container testID={tID('RiskAlertPopover')}>
      {isAddressed && !hideAddressedLabel && (
        <AddressedLabel testID={tID('RiskAlertPopover-addressed-label')}>
          <CheckIcon size={16} fillColor={colors.textSuccess} withCircle />
          <BodyText
            size={BodyTextSize.SMALL}
            color={colors.textSuccess}
            text={<FormattedMessage defaultMessage='Addressed' description='Risk addressed status label' />}
          />
        </AddressedLabel>
      )}
      <DetailedAlertView
        taskArguments={alert.taskArguments}
        taskType={alert.taskType}
        clientId={clientId}
        eventDate={alert.eventDate}
        isExpanded={isDetailExpanded}
        onToggleExpanded={() => setIsDetailExpanded((prev) => !prev)}
      />
      {isClientChartVersion && clientId && onSafetyPlanPress && (
        <SafetyPlanSection clientId={clientId} onPress={onSafetyPlanPress} />
      )}
      <ContactSection data={data} isClientChartVersion={isClientChartVersion} />
      {isClientChartVersion ? (
        onReviewRiskSummary && (
          <PrimaryButton
            testID={tID('RiskAlertPopover-review-risk-summary')}
            text={
              <FormattedMessage
                defaultMessage='Review full risk summary'
                description='Button to open the client risk summary from the risk alert popover'
              />
            }
            onPress={onReviewRiskSummary}
            fullWidth
          />
        )
      ) : (
        <ButtonRow>
          <FlexSecondaryButton
            testID={tID('RiskAlertPopover-log-note')}
            text={<FormattedMessage defaultMessage='Log general note' description='Log note button' />}
            onPress={onLogNote}
            fullWidth
          />

          {isAddressed ? (
            <FlexPrimaryButton
              testID={tID('RiskAlertPopover-hide-badge')}
              text={<FormattedMessage defaultMessage='Hide risk badge' description='Hide risk badge button' />}
              onPress={onHideRiskBadge ?? noop}
              fullWidth
            />
          ) : (
            <FlexPrimaryButton
              testID={tID('RiskAlertPopover-mark-addressed')}
              text={<FormattedMessage defaultMessage='Mark addressed' description='Mark addressed button' />}
              onPress={onMarkAddressed}
              fullWidth
            />
          )}
        </ButtonRow>
      )}
    </Container>
  )
}

export default RiskAlertPopover
